import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getApprovedLiveCategoryUrls,
  getLiveUrlReviewStatusClass,
  isFixtureOnlyLiveCategorySource,
  LIVE_URL_REVIEW_STATUS_LABELS,
  loadReviewedLiveCategorySource,
} from "./foodReviewedLiveUrlsHelpers";
import { formatDateTime } from "./foodRetailIngestionHelpers";
import AdminTableContainer from "../layout/AdminTableContainer";

const FoodReviewedLiveUrlsSection = ({ sourceKey }) => {
  const [sourcePayload, setSourcePayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadReviewedUrls = useCallback(async () => {
    if (!sourceKey) {
      setSourcePayload(null);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const { sourcePayload: payload } = await loadReviewedLiveCategorySource(sourceKey);
      setSourcePayload(payload);
    } catch (error) {
      setSourcePayload(null);
      setErrorMessage(error?.message || "Unable to load reviewed live URLs.");
    } finally {
      setLoading(false);
    }
  }, [sourceKey]);

  useEffect(() => {
    loadReviewedUrls();
  }, [loadReviewedUrls]);

  const approvedUrls = useMemo(
    () => getApprovedLiveCategoryUrls(sourcePayload),
    [sourcePayload]
  );
  const fixtureOnly = isFixtureOnlyLiveCategorySource(sourcePayload);

  return (
    <section className="panel-card food-retail-ingestion-page__reviewed-urls">
      <div className="food-retail-ingestion-page__section-header">
        <h2>Reviewed live URLs</h2>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={!sourceKey || loading}
          onClick={loadReviewedUrls}
        >
          Refresh
        </button>
      </div>

      <p className="muted">
        Live category ingestion is limited to legal/ops reviewed URLs. Scheduled ingestion
        remains disabled.
      </p>

      {loading ? <p className="muted">Loading reviewed URLs…</p> : null}
      {errorMessage ? <p className="food-retail-ingestion-page__form-errors">{errorMessage}</p> : null}

      {sourcePayload ? (
        <>
          <dl className="food-retail-ingestion-page__meta-grid">
            <div>
              <dt>Live category mode</dt>
              <dd>{sourcePayload.liveCategoryMode || "—"}</dd>
            </div>
            <div>
              <dt>Manual PoC allowed</dt>
              <dd>{approvedUrls.length ? "Yes (approved URLs only)" : "No"}</dd>
            </div>
            <div>
              <dt>Scheduled ingestion</dt>
              <dd>No</dd>
            </div>
            <div>
              <dt>Reviewed</dt>
              <dd>{formatDateTime(sourcePayload.reviewedAt)}</dd>
            </div>
          </dl>

          {fixtureOnly ? (
            <p className="food-retail-ingestion-page__banner food-retail-ingestion-page__banner--blocked">
              This source is fixture-only. Use verified fixtures for PoC runs; live category URLs
              are blocked.
            </p>
          ) : null}

          {!fixtureOnly && !approvedUrls.length ? (
            <p className="food-retail-ingestion-page__banner food-retail-ingestion-page__banner--blocked">
              No URLs are approved for manual PoC live ingestion yet. Update the reviewed manifest
              after legal/ops review.
            </p>
          ) : null}

          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Status</th>
                  <th>Target staples</th>
                  <th>Reviewed</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {(sourcePayload.urls || []).map((entry) => (
                  <tr key={entry.url}>
                    <td>{entry.url}</td>
                    <td>
                      <span className={getLiveUrlReviewStatusClass(entry.reviewStatus)}>
                        {LIVE_URL_REVIEW_STATUS_LABELS[entry.reviewStatus] || entry.reviewStatus}
                      </span>
                    </td>
                    <td>{(entry.targetStaples || []).join(", ") || "—"}</td>
                    <td>{formatDateTime(entry.reviewedAt)}</td>
                    <td>{entry.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </section>
  );
};

export default FoodReviewedLiveUrlsSection;
